import { Dropdown } from 'sharedUi/Dropdown';
import { useAccessibleProjects, projectOptions } from '../../hooks/useProjectScope';

interface ProjectFilterProps {
  /**
   * '' or null = every readable project, merged. Both are accepted because
   * callers hold this either way; the control always EMITS '', so a screen
   * whose state is `string | null` and one whose state is `string` can share it.
   */
  value: string | null;
  onChange: (appId: string) => void;
  /** Distinguishes the control in tests when a screen renders more than one. */
  testId?: string;
  allLabel?: string;
  minWidth?: number;
}

/**
 * The project picker every dashboard puts beside its other filters.
 *
 * One component rather than a copy per screen, because the rule it encodes is
 * the same everywhere: an empty value means "every project I can read, merged"
 * — the view these screens have always shown — and picking a project narrows
 * that, never widens it. The widening is refused server-side too; this is only
 * the control.
 *
 * Renders nothing when the caller can reach at most one project: a filter that
 * can only ever have one answer is noise.
 */
const ProjectFilter = ({ value, onChange, testId, allLabel = 'All projects', minWidth = 180 }: ProjectFilterProps) => {
  const { projects, canScopeByProject } = useAccessibleProjects();
  if (!canScopeByProject) return null;

  return (
    <Dropdown
      value={value ?? ''}
      options={projectOptions(projects, allLabel)}
      optionLabel="label"
      optionValue="value"
      placeholder={allLabel}
      showClear={!!value}
      style={{ minWidth }}
      onChange={(e: { value: string }) => onChange(e.value || '')}
      data-testid={testId ?? 'ProjectFilter'}
    />
  );
};

export default ProjectFilter;
